"use strict";

import _defineProperty from "@babel/runtime-corejs3/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime-corejs3/helpers/objectWithoutProperties";
var _Stack, _Fragment, _ContainedLayout;
const _excluded = ["children", "client", "optionalAuthentication", "logoUrl"],
  _excluded2 = ["Component", "FallbackComponent"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import "core-js/modules/es.array.iterator.js";
import "core-js/modules/es.promise.js";
import "core-js/modules/es.regexp.to-string.js";
import "core-js/modules/es.weak-map.js";
import "core-js/modules/esnext.weak-map.delete-all.js";
import "core-js/modules/web.dom-collections.iterator.js";
import { c as _c } from "react/compiler-runtime";
import { invariant } from '@autoguru/utilities';
import { createContext, useContext, useEffect, useMemo, useRef } from 'react';
import { NoResults } from '@autoguru/components';
import { BASE_URL } from '@autoguru/global-configs';
import { Button, Inline, Stack, Text } from '@autoguru/overdrive';
import { AsyncBoundary } from '@autoguru/suspense';
import { ContainedLayout } from '../../layout';
import { login, logout } from '../lib/behaviour';
import { cleaClinetBrowserCache, hasAuthParams } from '../lib/utils';
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment2 } from "react/jsx-runtime";
const context = /*#__PURE__*/createContext(null);
const handleCallBacks = function handleCallBacks(client, incomingResolve, incomingReject) {
  let failedCount = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : 0;
  const redirectPromiseHandler = (resolve, reject) => {
    client.handleRedirectCallback().then(state => resolve(state)).catch(() => {
      failedCount++;
      if (failedCount > 20) reject('Authentication failed ');else setTimeout(async () => {
        const isAuthed = await client.isAuthenticated();
        if (isAuthed) return resolve({
          isAuthed
        });
        // Try again in 300MS
        handleCallBacks(client, resolve, reject, failedCount);
      }, 500);
    });
  };
  let handleRedirectPromise;
  if (!incomingResolve) handleRedirectPromise = new Promise(redirectPromiseHandler);else redirectPromiseHandler(incomingResolve, incomingReject);
  return handleRedirectPromise;
};
const createAuthResource = (client, optionalAuthentication) => {
  let result = null;
  let error = null;
  const promiseChain = Promise.resolve().then(async () => {
    if (hasAuthParams()) {
      const {
        isAuthed,
        appState
      } = await handleCallBacks(client);
      isAuthed ? await client.checkSession() : window.history.replaceState({}, document.title, (appState === null || appState === void 0 ? void 0 : appState.returnTo) || window.location.toString());
    }
    return client.checkSession();
  }).then(async () => {
    const isAuthenticated = await client.isAuthenticated();
    result = {
      optionalAuthentication,
      isAuthenticated
    };
  }).catch(err => {
    error = err;
  });
  return () => {
    if (result !== null) return result;
    if (error !== null) throw error;
    throw promiseChain;
  };
};
const authClientCache = new WeakMap();
export const AuthProvider = _ref => {
  let {
      children,
      client,
      optionalAuthentication = false,
      logoUrl
    } = _ref,
    incomingOptions = _objectWithoutProperties(_ref, _excluded);
  const defaultOptions = useMemo(() => _objectSpread({
    allow_socials: false,
    logoUrl
  }, incomingOptions), [incomingOptions]);
  const authTracker = useRef(authClientCache.get(client));
  if (!authTracker.current) authClientCache.set(client,
  // biome-ignore lint/suspicious/noAssignInExpressions: migration quick fix. To be fixed when code is touched
  authTracker.current = createAuthResource(client, optionalAuthentication));
  return /*#__PURE__*/_jsx(context.Provider, {
    value: {
      read: authTracker.current,
      options: defaultOptions
    },
    children: children
  });
};

/**
 * Returns some auth related fields, note this will suspend till we know about its value.
 */
export const useAuth = () => {
  const $ = _c(5);
  const ctx = useContext(context);
  if (process.env.NODE_ENV !== "production") {
    !(ctx !== null) ? process.env.NODE_ENV !== "production" ? invariant(false, "Please define the AuthProvider in this app's tree") : invariant(false) : void 0;
  }
  let t0;
  if ($[0] !== ctx) {
    t0 = ctx.read();
    $[0] = ctx;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== ctx.options || $[3] !== t0) {
    t1 = _objectSpread(_objectSpread({}, t0), {}, {
      options: ctx.options
    });
    $[2] = ctx.options;
    $[3] = t0;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return t1;
};

/**
 * If the current auth session holds true that the user isnt logged in, then we redirect them to the login flow.
 *
 * Note the returned component will suspend till we know if they're logged in or not.
 */

export const withRequiredAuthentication = function withRequiredAuthentication(Component) {
  let showAuthErrorScreen = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
  let FallbackComponent = arguments.length > 2 ? arguments[2] : undefined;
  let FailedComponent = arguments.length > 3 ? arguments[3] : undefined;
  return props => /*#__PURE__*/_jsx(AsyncBoundary, {
    fallback: null,
    onError: cleaClinetBrowserCache,
    errorFallback: showAuthErrorScreen ? typeof FallbackComponent === 'undefined' ? /*#__PURE__*/_jsx(ContainedLayout, {
      width: "medium",
      children: /*#__PURE__*/_jsx(NoResults, {
        boxed: false,
        title: "Authentication Failed",
        children: /*#__PURE__*/_jsxs(Stack, {
          alignItems: "center",
          space: "3",
          children: [_Stack || (_Stack = /*#__PURE__*/_jsx(Stack, {
            space: "none",
            children: /*#__PURE__*/_jsx(Text, {
              size: "3",
              is: "p",
              align: "center",
              children: "Something went wrong during your authentication"
            })
          })), /*#__PURE__*/_jsxs(Inline, {
            noWrap: true,
            space: "2",
            alignX: "center",
            children: [/*#__PURE__*/_jsx(Button, {
              size: "small",
              variant: "primary",
              onClick: () => login(window.location.toString()),
              children: "Try Again"
            }), /*#__PURE__*/_jsx(Button, {
              size: "small",
              variant: "secondary",
              onClick: () => window.location.assign(BASE_URL),
              children: "Home"
            })]
          })]
        })
      })
    }) : FailedComponent : _Fragment || (_Fragment = /*#__PURE__*/_jsx(_Fragment2, {})),
    children: /*#__PURE__*/_jsx(AuthenticatedComp, _objectSpread(_objectSpread({}, props), {}, {
      Component: Component,
      FallbackComponent: FallbackComponent
    }))
  });
};
//@ts-ignore
const AuthenticatedComp = t0 => {
  const $ = _c(21);
  let Component;
  let FallbackComponent;
  let props;
  if ($[0] !== t0) {
    var _t = t0;
    ({
      Component,
      FallbackComponent
    } = _t);
    props = _objectWithoutProperties(_t, _excluded2);
    _t;
    $[0] = t0;
    $[1] = Component;
    $[2] = FallbackComponent;
    $[3] = props;
  } else {
    Component = $[1];
    FallbackComponent = $[2];
    props = $[3];
  }
  const {
    isAuthenticated,
    optionalAuthentication,
    options
  } = useAuth();
  let t1;
  if ($[4] !== isAuthenticated || $[5] !== optionalAuthentication || $[6] !== options) {
    t1 = () => {
      if (!isAuthenticated && !optionalAuthentication) {
        login(options.returnTo || window.location.toString(), options);
      }
    };
    $[4] = isAuthenticated;
    $[5] = optionalAuthentication;
    $[6] = options;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  let t2;
  if ($[8] !== isAuthenticated || $[9] !== optionalAuthentication || $[10] !== options.returnTo) {
    t2 = [options.returnTo, isAuthenticated, optionalAuthentication];
    $[8] = isAuthenticated;
    $[9] = optionalAuthentication;
    $[10] = options.returnTo;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  useEffect(t1, t2);
  let t3;
  if ($[12] !== options) {
    t3 = () => {
      if (process.env.NODE_ENV !== "production" && process.__browser__) {
        window.logout = _temp;
        window.login = () => void login(window.location.toString(), options);
      }
    };
    $[12] = options;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  let t4;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = [];
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  useEffect(t3, t4);
  let t5;
  if ($[15] !== Component || $[16] !== FallbackComponent || $[17] !== isAuthenticated || $[18] !== optionalAuthentication || $[19] !== props) {
    t5 = isAuthenticated || optionalAuthentication ? /*#__PURE__*/_jsx(Component, _objectSpread({}, props)) : typeof FallbackComponent === "undefined" ? _ContainedLayout || (_ContainedLayout = /*#__PURE__*/_jsx(ContainedLayout, {
      children: /*#__PURE__*/_jsx(Text, {
        align: "center",
        children: "Redirecting..."
      })
    })) : FallbackComponent;
    $[15] = Component;
    $[16] = FallbackComponent;
    $[17] = isAuthenticated;
    $[18] = optionalAuthentication;
    $[19] = props;
    $[20] = t5;
  } else {
    t5 = $[20];
  }
  return t5;
};
function _temp() {
  return void logout(window.location.toString());
}